import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Box,
  Flex,
  Card,
  Text,
  Grid,
  Title,
  NumberFormatter,
  Button,
  LoadingOverlay,
  Space,
} from "@mantine/core";
import axios from "axios";

import { getPlacementImage } from "@components/Asset/helpers";
import { placementDefs } from "@components/Asset/placementDefs";
import { ImageSlider } from "@components/shared";
import { size } from "@util/breakpoints";
import { Speedometer } from "@components/DataViz";
import { Odometer } from "@components/RideChallenge";
import {
  StyledImage,
  StyledPlaceholder,
} from "@components/RideChallengeApp/UserHistory";
import RideChallengeHeader from "@components/RideChallengeApp/RideChallengeHeader";
import logo from "@assets/images/odometer-contest-heading_480.png";
import { sampleManifest } from "./sampleManifest";
const backgroundColor = `rgba(0, 0, 0, 0.925)`;

export default function EffortPublicDashboardDynamic({ codeData }) {
  useEffect(() => {
    if (!codeData) return;
    runSetup();
  }, [JSON.stringify(codeData)]);
  const { dashboard_data: dashboardData } = codeData;
  const components = dashboardData;

  const appConfig = codeData.published_app_configuration;
  const config = codeData.published_configuration;
  const baseColor = appConfig.colors?.base || "orange";

  function runSetup() {
    document.body.style.backgroundColor = "var(--mantine-color-dark-9)";
    const backgroundImage = getPlacementImage(
      appConfig,
      codeData.assets,
      placementDefs.background
    );

    if (backgroundImage) {
      document.body.style.background = `url(${backgroundImage}) no-repeat center center`;
      document.body.style.backgroundSize = "cover";
      document.body.style.backgroundAttachment = "fixed";
    }
  }

  const assets = config.assets
    ? config.assets
        .map((m) => ({
          ...m,
          asset: codeData.assets?.find((f) => f.id === m.id),
        }))
        .filter((f) => f.asset)
    : [].filter((f) => f.asset);

  return (
    <div>
      <Layout>
        <Wrapper>
          <Box p="lg">
            <Box mt="lg">
              <DealerInfo>
                <img src={logo} alt="Let's Ride" className="rc-logo" />
              </DealerInfo>
            </Box>
            {components.map((item, index) => (
              <Box key={index} mt={index === 0 ? "xs" : "lg"}>
                {(item.title || item.description) &&
                  item.title_position !== "bottom" && (
                    <SectionTitle
                      title={item.title}
                      description={item.description}
                    />
                  )}
                {item.component === "image" && (
                  <ImageContainer>
                    <img src={item.data.image_url} alt={item.data.alt_text} />
                  </ImageContainer>
                )}
                {item.component === "title" && (
                  <SectionTitle
                    title={item.data.title}
                    description={
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            "Plus, every mile ridden supports our heroes<br />March 28 - October 31, 2025",
                        }}
                      />
                    }
                    primary
                    color={baseColor}
                  />
                )}
                {item.component === "speedometer" && (
                  <GaugeGoalWrapper>
                    <Speedometer
                      value={item.data.value}
                      maxValue={item.data.max_value}
                      fillProgress
                    />
                  </GaugeGoalWrapper>
                )}
                {item.component === "odometer" && (
                  <OdometerWrapper>
                    <Odometer newValue={item.data.value} />
                  </OdometerWrapper>
                )}
                {item.component === "spacer" && <Space h={item.h || "xl"} />}
                {item.component === "meta_blocks" && (
                  <MetaBlocks data={item.data} color={baseColor} />
                )}
                {item.component === "meta_block" && (
                  <MetaBlock data={item.data} color={baseColor} />
                )}
                {item.component === "leaderboard" && (
                  <Leaderboard color={baseColor} items={item.data} />
                )}
                {(item.title || item.description) &&
                  item.title_position === "bottom" && (
                    <Box mt="xs">
                      <SectionTitle
                        title={item.title}
                        description={item.description}
                      />
                    </Box>
                  )}
              </Box>
            ))}
          </Box>
          <Text c="dimmed" align="center" size="xs" pb="xl">
            Do not break the law or endanger yourself or others when riding or
            otherwise participating in this promotion.
          </Text>
        </Wrapper>
      </Layout>
    </div>
  );
}

const MetaBlocks = ({ data, color }) => {
  return (
    <Grid>
      {data.map((statBlock, i) => (
        <Grid.Col span={6} key={i}>
          <MetaBlock data={statBlock} color={color} />
        </Grid.Col>
      ))}
    </Grid>
  );
};

const MetaBlock = ({ data, color }) => {
  return (
    <StatBlock color={color}>
      <div>
        <Text size="xs" align="center" className="label">
          {data.text}
        </Text>
        <Title mt={4} order={2} c="white" align="center" className="value">
          {typeof data.value === "number" ? (
            <NumberFormatter value={data.value} thousandSeparator />
          ) : (
            data.value
          )}
        </Title>
      </div>
    </StatBlock>
  );
};

const Layout = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: 60px 0 20px 0;
  word-wrap: break-word;
  overflow-x: hidden;

  label {
    color: whitesmoke;
  }

  @media (max-width: ${size.tablet}) {
    width: 97%;
    padding-top: 10px;
  }
`;

const GaugeGoalWrapper = styled.div`
  position: relative;
  p {
    color: #fff;
    font-weight: 600;
    font-size: 20px;
    text-transform: uppercase;
    text-align: center;
    position: relative;
    margin-top: 0;
    margin-bottom: 0;
    top: -20px;
  }
`;

const OdometerWrapper = styled.div`
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  flex-direction: column;

  .odometer-value {
    /* font-size: 40px; */
  }
`;

const StatBlock = styled.div`
  background: var(--mantine-color-dark-8);
  border: 1px solid var(--mantine-color-dark-5);
  border-radius: 10px;
  padding: 10px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;

  .value {
    font-weight: 700;
  }

  .label {
    color: ${(props) => `var(--mantine-color-${props.color}-7)`};
    text-transform: uppercase;
    font-weight: 600;
  }
`;

const Leaderboard = ({ color, items }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(items[0].name);

  const tabItem = items.find((item) => item.name === selectedTab);

  const tabs = items.map((item) => ({
    label: item.tab_button_label,
    value: item.name,
  }));

  if (!tabItem) return null;

  return (
    <div>
      <SectionTitle title={tabItem.title} description={tabItem.description} />
      <Box style={{ position: "relative" }}>
        {tabs.length > 1 && (
          <Flex gap="xs" justify="center" mb="xl">
            {tabs.map((tab, index) => (
              <Button
                key={index}
                color={color}
                variant={selectedTab === tab.value ? "filled" : "light"}
                size="xs"
                radius="lg"
                onClick={() => setSelectedTab(tab.value)}
              >
                {tab.label}
              </Button>
            ))}
          </Flex>
        )}
        {tabItem.results.map((item, index) => (
          <CardContainer key={item.location_id}>
            <Flex gap="sm" align="center">
              <Title miw={18} order={5} c="white">
                {index + 1}
              </Title>
              {item.logo_url ? (
                <StyledImage height={40} width={40} src={item.logo_url} />
              ) : (
                <StyledPlaceholder height={40} width={40} />
              )}
              <div style={{ flexGrow: 1 }}>
                <Text size="sm" fw={600} c={`${color}.7`}>
                  {item.name}
                </Text>
                <Text size="sm" c="dimmed">
                  {item.city}, {item.state}
                </Text>
              </div>
              <Text
                size="xs"
                fw={600}
                c="dimmed"
                style={{
                  flexGrow: 1,
                  textAlign: "right",
                }}
              >
                <NumberFormatter value={item.value} thousandSeparator />
                {tabItem.unit && <>{tabItem.unit}</>}
              </Text>
            </Flex>
          </CardContainer>
        ))}
        {!loading && tabItem.results.length === 0 && (
          <Flex justify="center" align="center">
            <Text fw={600} c="white" align="center">
              Check back later for leaderboard results
            </Text>
          </Flex>
        )}
      </Box>
    </div>
  );
};

const CardContainer = ({ children }) => (
  <Card
    mb="xs"
    style={{
      background: "var(--mantine-color-dark-8)",
      // borderWidth: 4,
      borderColor: "var(--mantine-color-dark-5)",
    }}
  >
    {children}
  </Card>
);

const Wrapper = styled.div`
  /* background-color: ${backgroundColor}; */
  border: 1px solid var(--mantine-color-dark-7);
  padding-bottom: ${(props) => (props.isDealer ? "1em" : "0")};
  background: ${backgroundColor};
  border-radius: ${(props) =>
    true
      ? "var(--mantine-radius-md)"
      : "var(--mantine-radius-md) var(--mantine-radius-md) 0 0"};
`;

const DealerInfo = styled.div`
  img.dealer-logo {
    margin: 0 auto;
    display: block;
    max-width: 200px;
    max-height: 125px;
    object-fit: contain;
  }

  img.rc-logo {
    margin: 0 auto;
    display: block;
    /* width: 350px; */
    max-width: 100%;
    /* max-width: 200px; */
    max-height: 400px;
    object-fit: contain;
  }
`;

function getScaledGaugeMaxValue(currentValue) {
  return 50000000;
  if (currentValue < 100000) {
    return 100000;
  } else if (currentValue < 1000000) {
    return 1000000;
  } else if (currentValue < 10000000) {
    return 10000000;
  } else if (currentValue < 50000000) {
    return 50000000;
  }
  return 100000000;
}

const SectionTitle = ({
  title,
  description,
  primary = false,
  color = "white",
}) => {
  if (!title && !description) return null;

  return (
    <div>
      {title && (
        <Title
          // order={primary ? 1 : 2}
          order={3}
          c={color}
          align="center"
          mb={description ? 0 : "lg"}
        >
          {title}
        </Title>
      )}
      {description && (
        <Text align="center" c="white" size={primary ? "md" : "xs"} mb="lg">
          {description}
        </Text>
      )}
    </div>
  );
};

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 95%;
    max-height: 90%;
    object-fit: contain;
    border-radius: var(--mantine-radius-xs);
  }
`;
